import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Shield, MapPin, Settings, ChevronRight, HelpCircle } from 'lucide-react';
import { getProductBySlug, getProductsByCategory } from '../data/products';
import { ProductCard } from '../components/ProductCard';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = getProductBySlug(slug || '');
  const [activeTab, setActiveTab] = useState<'features' | 'specs' | 'includes'>('features');
  if (!product) {
    return <Navigate to="/shop" replace data-id="element-552" />;
  }
  // Get some related products (same category, excluding current)
  const relatedProducts = getProductsByCategory(product.category).filter(p => p.id !== product.id).slice(0, 3);
  const isModuleOnly = product.category === 'Modules';
  return <div className="bg-white" data-id="element-553">
      {/* Breadcrumbs */}
      <div className="bg-gray-50 border-b border-gray-200 py-3" data-id="element-554">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500" data-id="element-555">
          <Link to="/" className="hover:text-yarbo-dark" data-id="element-556">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-557" />
          <Link to="/shop" className="hover:text-yarbo-dark" data-id="element-558">
            Shop
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-559" />
          <span className="text-yarbo-dark font-medium truncate" data-id="element-560">
            {product.name}
          </span>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 lg:py-20" data-id="element-561">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-562">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-563">
            {/* Image Gallery */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="space-y-4" data-id="element-564">
              <div className="aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden border border-gray-200" data-id="element-565">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-566" />
              </div>
              {/* Thumbnail placeholders */}
              <div className="grid grid-cols-4 gap-4" data-id="element-567">
                {[1, 2, 3, 4].map(i => <div key={i} className="aspect-square bg-gray-100 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:border-yarbo-yellow" data-id="element-568">
                    <img src={product.image} alt="thumbnail" className="w-full h-full object-cover opacity-70 hover:opacity-100 transition-opacity" data-id="element-569" />
                  </div>)}
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex flex-col" data-id="element-570">
              {isModuleOnly && <div className="bg-blue-50 text-blue-800 text-sm font-bold px-4 py-2 rounded-lg mb-6 inline-flex items-center w-fit" data-id="element-571">
                  <Settings className="w-4 h-4 mr-2" data-id="element-572" />
                  Module Only - Requires Yarbo Core
                </div>}

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-yarbo-dark mb-4 leading-tight" data-id="element-573">
                {product.name}
              </h1>

              <div className="text-3xl font-bold text-yarbo-dark mb-2" data-id="element-574">
                ${product.price.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mb-6" data-id="element-575">
                *Editable Price Placeholder. Contact us for current Michigan
                pricing.
              </p>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-576">
                {product.shortDescription}
              </p>

              <div className="space-y-4 mb-10" data-id="element-577">
                {product.features.slice(0, 4).map((feature, idx) => <div key={idx} className="flex items-start" data-id="element-578">
                    <Check className="w-5 h-5 text-yarbo-green mr-3 mt-0.5 flex-shrink-0" data-id="element-579" />
                    <span className="text-gray-700 font-medium" data-id="element-580">{feature}</span>
                  </div>)}
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-8" data-id="element-581">
                <h3 className="font-bold text-yarbo-dark mb-4 flex items-center" data-id="element-582">
                  <MapPin className="w-5 h-5 mr-2 text-yarbo-yellow" data-id="element-583" />
                  Michigan Retailer Benefits
                </h3>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-584">
                  <li data-id="element-585">• Local setup guidance and support</li>
                  <li data-id="element-586">• Expert advice for Michigan weather conditions</li>
                  <li data-id="element-587">• Ask about local demo availability</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-auto" data-id="element-588">
                <Link to="/contact" className="btn-primary flex-1 py-4 text-lg" data-id="element-589">
                  Request a Quote
                </Link>
                <Link to="/installation-demos" className="btn-secondary flex-1 py-4 text-lg" data-id="element-590">
                  Ask About Setup
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="border-t border-gray-200 bg-gray-50 py-16" data-id="element-591">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-592">
          <div className="flex space-x-1 bg-gray-200 p-1 rounded-xl mb-8 overflow-x-auto hide-scrollbar" data-id="element-593">
            <button onClick={() => setActiveTab('features')} className={`flex-1 py-3 px-4 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${activeTab === 'features' ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600 hover:text-yarbo-dark'}`} data-id="element-594">
              Key Features
            </button>
            <button onClick={() => setActiveTab('includes')} className={`flex-1 py-3 px-4 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${activeTab === 'includes' ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600 hover:text-yarbo-dark'}`} data-id="element-595">
              What's Included
            </button>
            <button onClick={() => setActiveTab('specs')} className={`flex-1 py-3 px-4 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${activeTab === 'specs' ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600 hover:text-yarbo-dark'}`} data-id="element-596">
              Michigan Use Case
            </button>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 min-h-[300px]" data-id="element-597">
            {activeTab === 'features' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-598">
                <h3 className="text-2xl font-bold text-yarbo-dark mb-6" data-id="element-599">
                  System Features
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-600">
                  {product.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-601">
                      <div className="bg-yarbo-yellow/20 p-2 rounded-full mr-4 mt-1" data-id="element-602">
                        <Check className="w-4 h-4 text-yarbo-dark" data-id="element-603" />
                      </div>
                      <span className="text-gray-700 leading-relaxed" data-id="element-604">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </motion.div>}

            {activeTab === 'includes' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-605">
                <h3 className="text-2xl font-bold text-yarbo-dark mb-6" data-id="element-606">
                  In The Box
                </h3>
                {isModuleOnly && <p className="text-red-600 font-medium mb-6 p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-607">
                    Note: This is a module only. The Yarbo Core is required for
                    operation and is sold separately or as part of a Complete
                    System.
                  </p>}
                <ul className="space-y-4" data-id="element-608">
                  {product.includes.map((item, idx) => <li key={idx} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-609">
                      <Shield className="w-5 h-5 text-gray-400 mr-4" data-id="element-610" />
                      <span className="font-medium text-gray-800" data-id="element-611">{item}</span>
                    </li>)}
                </ul>
              </motion.div>}

            {activeTab === 'specs' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-612">
                <h3 className="text-2xl font-bold text-yarbo-dark mb-6" data-id="element-613">
                  Built for Michigan
                </h3>
                <div className="prose prose-lg text-gray-600" data-id="element-614">
                  <p data-id="element-615">
                    Whether you're dealing with heavy lake-effect snow in West
                    Michigan, sprawling rural acreage Up North, or a demanding
                    suburban lawn in Metro Detroit, the {product.name} is
                    engineered to handle it.
                  </p>
                  <p className="mt-4" data-id="element-616">
                    Unlike generic robotic mowers that struggle with Midwest
                    conditions, Yarbo's robust tracks and powerful motors
                    provide the traction needed for steep slopes and wet
                    conditions. The wire-free RTK navigation means you don't
                    have to worry about frost heaves breaking perimeter wires
                    during Michigan winters.
                  </p>
                  <div className="mt-8 p-6 bg-yarbo-charcoal text-white rounded-xl" data-id="element-617">
                    <h4 className="text-xl font-bold mb-2 text-yarbo-yellow" data-id="element-618">
                      Need help planning your setup?
                    </h4>
                    <p className="mb-4 text-gray-300 text-sm" data-id="element-619">
                      Every property is unique. We can help determine if this
                      system is right for your specific yard layout.
                    </p>
                    <Link to="/contact" className="btn-primary py-2 px-6 text-sm" data-id="element-620">
                      Contact Us
                    </Link>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && <section className="py-20 bg-white border-t border-gray-200" data-id="element-621">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-622">
            <h2 className="text-3xl font-extrabold text-yarbo-dark mb-10 text-center" data-id="element-623">
              You May Also Consider
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-624">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-625" />)}
            </div>
          </div>
        </section>}
    </div>;
}